// AuthProvider owns the user-facing auth state:
//   - the in-memory `profile` (null while unauthenticated)
//   - the boot-time silent-refresh on page reload (via httpOnly cookie)
//   - login/logout flows
//   - reacting to API-side auth invalidation (401 after refresh fails)
//
// Components read `useAuth()` to gate routes — there is no global store
// library; React Context is enough for this surface.

import { createContext, useCallback, useContext, useEffect, useState } from 'react'
import type { ProfileResponse } from '@/types/api'
import { APIError, onAuthInvalidated, request, setAccessToken } from '@/lib/api'

interface AuthContextValue {
  ready: boolean   // false during the initial silent-refresh attempt
  profile: ProfileResponse | null
  // Push a fresh ProfileResponse into the global auth state — used by the
  // operator's own Profile page after a self-edit (PATCH /users/me returns the
  // refreshed profile) so the nav / header reflect the new name immediately.
  setProfile: (profile: ProfileResponse) => void
  login: (email: string, password: string) => Promise<void>
  logout: () => Promise<void>
}

const AuthContext = createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [ready, setReady] = useState(false)
  const [profile, setProfile] = useState<ProfileResponse | null>(null)

  // Boot — try silent refresh + profile fetch. If the httpOnly cookie
  // is missing or expired, both calls fail and we land at Login.
  useEffect(() => {
    let cancelled = false
    ;(async () => {
      try {
        // performRefresh inside api.ts will quietly populate the
        // in-memory access token on success.
        const refresh = await fetch('/api/auth/refresh', {
          method: 'POST',
          credentials: 'include',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({}),
        })
        if (refresh.ok) {
          const data = (await refresh.json()) as { access_token: string }
          setAccessToken(data.access_token)
          const me = await request<ProfileResponse>('/profile')
          if (!cancelled) setProfile(me)
        }
      } catch {
        // Stay logged-out on any boot failure — Login page will handle it.
      } finally {
        if (!cancelled) setReady(true)
      }
    })()
    return () => { cancelled = true }
  }, [])

  // API-side invalidation (401 after a failed refresh) → drop profile.
  useEffect(() => {
    return onAuthInvalidated(() => setProfile(null))
  }, [])

  const login = useCallback(async (email: string, password: string) => {
    const token = await request<{ access_token: string }>(
      '/auth/login',
      {
        method: 'POST',
        body: { email, password },
        query: { client_type: 'admin' },
      },
    )
    setAccessToken(token.access_token)
    const me = await request<ProfileResponse>('/profile')
    // PX-73: any privileged role passes (non-empty permissions list).
    // Plain `user` role carries [] and is rejected at the gate.
    if (!Array.isArray(me.permissions) || me.permissions.length === 0) {
      setAccessToken(null)
      throw new APIError(
        403,
        { code: 'PERMISSION_DENIED', message: 'A privileged role is required to sign in' },
        'A privileged role is required to sign in',
      )
    }
    setProfile(me)
  }, [])

  const logout = useCallback(async () => {
    try {
      await request('/auth/logout', { method: 'POST', body: {} })
    } catch {
      // best-effort — the server-side refresh-token revoke is the goal;
      // local cleanup happens regardless.
    }
    setAccessToken(null)
    setProfile(null)
  }, [])

  return (
    <AuthContext.Provider value={{ ready, profile, setProfile, login, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = useContext(AuthContext)
  if (ctx === null) throw new Error('useAuth() must be used inside <AuthProvider>')
  return ctx
}
